import { useState, useRef, useEffect } from "react";
import { useForm } from "@formspree/react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { trackEvent } from "../lib/analytics";

const STATES = [
  "Andhra Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jammu & Kashmir", "Jharkhand",
  "Karnataka", "Kerala", "Ladakh", "Madhya Pradesh", "Maharashtra",
  "Odisha", "Punjab", "Rajasthan", "Tamil Nadu", "Telangana",
  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Other",
];

const BUDGETS = [
  "Under ₹50,000",
  "₹50,000 – ₹1,00,000",
  "₹1,00,000 – ₹2,50,000",
  "₹2,50,000+",
  "Not sure yet",
];

const LEAD_METHODS = [
  "Referrals & word of mouth",
  "Instagram / Facebook (organic)",
  "Paid ads (self-managed)",
  "Paid ads (agency / freelancer)",
  "Listing platforms",
  "Not doing any yet",
  "Other",
];

const REFERRALS = [
  "Google search",
  "Instagram",
  "Facebook",
  "Referral",
  "WhatsApp",
  "Other",
];

const SERVICES = [
  "Hajj Marketing",
  "Umrah Marketing",
  "Meta Ads",
  "Google Ads",
  "Social Media Marketing",
  "WhatsApp Marketing",
  "Email Marketing",
  "Full Growth Strategy",
];

const EMPTY = {
  full_name: "",
  agency_name: "",
  phone: "",
  whatsapp_number: "",
  email: "",
  city: "",
  state: "",
  website: "",
  monthly_budget: "",
  current_lead_method: "",
  biggest_challenge: "",
  referral_source: "",
};

const inputCls =
  "w-full bg-transparent border-0 border-b border-charcoal/20 focus:border-gold rounded-none px-0 py-3 text-sm text-charcoal placeholder:text-charcoal/35 outline-none transition-colors duration-300";
const labelCls =
  "block text-[11px] font-semibold tracking-[0.2em] uppercase text-charcoal/55 mb-1";

const Field = ({ label, required, children, testid }) => (
  <div data-testid={testid}>
    <label className={labelCls}>
      {label} {required && <span className="text-tan">*</span>}
    </label>
    {children}
  </div>
);

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [services, setServices] = useState([]);
  const [state, handleSubmit] = useForm("xvkovrpk");
  const started = useRef(false);
  const successShown = useRef(false);
  const errorShown = useRef(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onFirstFocus = () => {
    if (!started.current) {
      started.current = true;
      trackEvent("form_start", { form: "strategy_call" });
    }
  };

  const toggleService = (s) =>
    setServices((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );

  const submit = async (e) => {
    e.preventDefault();
    if (state.submitting) return;
    await handleSubmit(e);
  };

  useEffect(() => {
    if (state.succeeded && !successShown.current) {
      successShown.current = true;
      trackEvent("form_submit", { form: "strategy_call" });
      toast.success("Enquiry received. We'll review it and get back to you.");
      setForm(EMPTY);
      setServices([]);
      started.current = false;
    }
    if (!state.succeeded) successShown.current = false;
  }, [state.succeeded]);

  useEffect(() => {
    if (state.errors && !state.succeeded && !errorShown.current) {
      errorShown.current = true;
      toast.error("Something went wrong. Please try again or email us directly.");
    }
    if (!state.errors) errorShown.current = false;
  }, [state.errors, state.succeeded]);

  return (
    <form
      onSubmit={submit}
      onFocusCapture={onFirstFocus}
      data-testid="contact-enquiry-form"
      className="space-y-8"
    >
      <input
        type="hidden"
        name="services_interested"
        value={services.join(", ")}
      />
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
        <Field label="Full Name" required testid="field-full-name">
          <input required name="full_name" value={form.full_name} onChange={set("full_name")} placeholder="Your full name" className={inputCls} data-testid="input-full-name" />
        </Field>
        <Field label="Agency Name" testid="field-agency-name">
          <input name="agency_name" value={form.agency_name} onChange={set("agency_name")} placeholder="Your travel agency" className={inputCls} data-testid="input-agency-name" />
        </Field>
        <Field label="Phone Number" required testid="field-phone">
          <input required name="phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" className={inputCls} data-testid="input-phone" />
        </Field>
        <Field label="WhatsApp Number" testid="field-whatsapp">
          <input name="whatsapp_number" type="tel" value={form.whatsapp_number} onChange={set("whatsapp_number")} placeholder="+91 98765 43210" className={inputCls} data-testid="input-whatsapp-number" />
        </Field>
        <Field label="Email" required testid="field-email">
          <input required name="email" type="email" value={form.email} onChange={set("email")} placeholder="you@agency.in" className={inputCls} data-testid="input-email" />
        </Field>
        <Field label="Website" testid="field-website">
          <input name="website" value={form.website} onChange={set("website")} placeholder="https://" className={inputCls} data-testid="input-website" />
        </Field>
        <Field label="City" testid="field-city">
          <input name="city" value={form.city} onChange={set("city")} placeholder="e.g. Mumbai, Surat, Hyderabad" className={inputCls} data-testid="input-city" />
        </Field>
        <Field label="State" testid="field-state">
          <select name="state" value={form.state} onChange={set("state")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="input-state">
            <option value="">Select state</option>
            {STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Field>
        <Field label="Monthly Advertising Budget" testid="field-budget">
          <select name="monthly_budget" value={form.monthly_budget} onChange={set("monthly_budget")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="input-budget">
            <option value="">Select a range</option>
            {BUDGETS.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </Field>
        <Field label="Current Lead Generation Method" testid="field-lead-method">
          <select name="current_lead_method" value={form.current_lead_method} onChange={set("current_lead_method")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="input-lead-method">
            <option value="">Select one</option>
            {LEAD_METHODS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Services Interested In" testid="field-services">
        <div className="flex flex-wrap gap-2 pt-2">
          {SERVICES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => toggleService(s)}
              data-testid={`service-chip-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`px-4 py-2 text-xs font-semibold tracking-wide border transition-colors duration-300 ${
                services.includes(s)
                  ? "bg-tan text-cream border-tan"
                  : "bg-transparent text-charcoal/60 border-charcoal/20 hover:border-gold"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Biggest Marketing Challenge" testid="field-challenge">
        <textarea
          rows={4}
          name="biggest_challenge"
          value={form.biggest_challenge}
          onChange={set("biggest_challenge")}
          placeholder="Tell us what's holding your agency's growth back: enquiry volume, lead quality, follow-up, seasonality…"
          className={`${inputCls} resize-none`}
          data-testid="input-challenge"
        />
      </Field>

      <Field label="How Did You Hear About Us?" testid="field-referral">
        <select name="referral_source" value={form.referral_source} onChange={set("referral_source")} className={`${inputCls} appearance-none cursor-pointer max-w-sm`} data-testid="input-referral">
          <option value="">Select one</option>
          {REFERRALS.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </Field>

      <button
        type="submit"
        disabled={state.submitting}
        data-testid="contact-form-submit-button"
        className="group inline-flex items-center gap-2 bg-tan text-cream text-sm font-semibold tracking-wide px-8 py-4 transition-colors duration-300 hover:bg-charcoal disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {state.submitting ? "Sending…" : "Request a Strategy Call"}
        <ArrowUpRight
          size={16}
          strokeWidth={1.5}
          className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </button>
      <p className="text-xs text-charcoal/45 leading-relaxed max-w-lg">
        By submitting, you agree to be contacted about your enquiry. Your
        details are used only to respond to your request.
      </p>
    </form>
  );
};
